import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MessageCircle, Send, X, Loader2, MailQuestion } from "lucide-react";
import { API } from "@/lib/api";

const SID_KEY = "paqvero_support_sid";
const QUICK_CHIPS = ["Track my order", "What is PAQVERO Credit?", "Delivery charges?", "Where do you deliver?"];

function LeadForm({ sessionId, prefill, onDone }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState(prefill);
  const [website, setWebsite] = useState(""); // honeypot — hidden from humans
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      const res = await fetch(`${API}/support/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: name.trim(), email: email.trim(), message: message.trim(), session_id: sessionId, website }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Please check your details and try again.");
      onDone(data.follow_up);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const field = "h-10 w-full rounded-lg border border-line bg-input px-3 text-sm text-1 placeholder:text-[var(--pq-text3)] focus:border-amber-500/60 focus:outline-none";
  return (
    <form onSubmit={submit} data-testid="lead-form"
      className="rounded-2xl border border-amber-500/25 bg-amber-500/[0.05] p-4">
      <p className="mb-3 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-amber-300">
        <MailQuestion className="h-4 w-4" /> Contact PAQVERO
      </p>
      <div className="space-y-2">
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" required minLength={2} maxLength={80} data-testid="lead-name" className={field} />
        <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="Your email" required maxLength={120} data-testid="lead-email" className={field} />
        <textarea value={message} onChange={(e) => setMessage(e.target.value)} placeholder="How can the team help?" required minLength={10} maxLength={2000} rows={3} data-testid="lead-message"
          className="w-full rounded-lg border border-line bg-input px-3 py-2 text-sm text-1 placeholder:text-[var(--pq-text3)] focus:border-amber-500/60 focus:outline-none" />
        <input value={website} onChange={(e) => setWebsite(e.target.value)} tabIndex={-1} autoComplete="off" aria-hidden="true"
          className="absolute -left-[9999px] h-0 w-0 opacity-0" />
      </div>
      {error && <p data-testid="lead-error" className="mt-2 text-xs text-red-400">{error}</p>}
      <div className="mt-3 flex items-center gap-2">
        <button type="submit" disabled={busy} data-testid="lead-submit"
          className="flex h-9 items-center gap-2 rounded-lg bg-amber-500 px-4 text-xs font-bold text-black transition-colors hover:bg-amber-400 disabled:opacity-40">
          {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Send to the team
        </button>
        <button type="button" onClick={() => onDone(null)} data-testid="lead-dismiss"
          className="h-9 rounded-lg border border-line px-3 text-xs text-2 hoverable-t1">
          No thanks
        </button>
      </div>
    </form>
  );
}

function getSessionId() {
  let sid = localStorage.getItem(SID_KEY);
  if (!sid) {
    sid = crypto.randomUUID().replace(/-/g, "");
    localStorage.setItem(SID_KEY, sid);
  }
  return sid;
}

export default function SupportChat() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [leadOffer, setLeadOffer] = useState(false);
  const [leadDone, setLeadDone] = useState(null); // null | {followUp: bool} | "dismissed"
  const scrollRef = useRef(null);
  const sid = useRef(null);

  useEffect(() => {
    sid.current = getSessionId();
    setSessionId(sid.current);
  }, []);

  // Global "Support" entry points (navbar account menu) open the chat from anywhere.
  useEffect(() => {
    const openChat = () => setOpen(true);
    window.addEventListener("paqvero:open-chat", openChat);
    return () => window.removeEventListener("paqvero:open-chat", openChat);
  }, []);


  // restore transcript when opened
  useEffect(() => {
    if (!open || !sessionId) return;
    const token = localStorage.getItem("packo_token");
    fetch(`${API}/support/history?session_id=${sessionId}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((r) => r.json())
      .then((d) => setMessages(d.messages || []))
      .catch(() => {});
  }, [open, sessionId]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, streaming]);

  const send = async (text) => {
    const message = (text ?? input).trim();
    if (!message || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: message }, { role: "assistant", text: "" }]);
    setStreaming(true);
    try {
      const token = localStorage.getItem("packo_token");
      const res = await fetch(`${API}/support/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ message, session_id: sid.current }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Support is unavailable right now — please email care@paqvero.in.");
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const events = buf.split("\n\n");
        buf = events.pop();
        for (const ev of events) {
          const line = ev.startsWith("data: ") ? ev.slice(6) : ev;
          if (!line) continue;
          let frame;
          try { frame = JSON.parse(line); } catch { continue; }
          if (frame.done) continue;
          if (frame.lead_offer) { setLeadOffer(true); continue; }
          const chunk = frame.error ? frame.error : frame.t || "";
          setMessages((m) => {
            const copy = [...m];
            copy[copy.length - 1] = { role: "assistant", text: copy[copy.length - 1].text + chunk };
            return copy;
          });
        }
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", text: e.message };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <>
      {/* floating launcher */}
      <motion.button
        data-testid="support-chat-button"
        onClick={() => setOpen((o) => !o)}
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 1.2, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        aria-label="Open PAQVERO Support chat"
        className="fixed bottom-[84px] right-5 z-[70] border-none bg-transparent p-0 md:bottom-5"
      >
        <span className="pq-shiny h-14 shadow-[0_8px_30px_rgba(255,159,28,0.25)]">
          <span className="pq-shiny__content h-full pl-4 pr-5 text-sm font-bold">
            {open ? <X className="h-5 w-5" /> : <MessageCircle className="h-5 w-5" />}
            {!open && <span>Support</span>}
          </span>
        </span>
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="support-chat-panel"
            initial={{ opacity: 0, y: 24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.97 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="fixed inset-x-3 bottom-[84px] top-24 z-[70] flex flex-col overflow-hidden rounded-2xl border border-line bg-surface shadow-2xl sm:inset-x-auto sm:bottom-24 sm:right-6 sm:top-auto sm:h-[560px] sm:w-[380px]"
          >
            {/* header */}
            <div className="flex items-center gap-3 border-b border-[#E4B66D]/25 bg-[#242521] px-5 py-4">
              <img src="/paqvero-logo.png" alt="PAQVERO" className="h-5 w-auto" />
              <div className="flex-1">
                <p className="text-sm font-bold text-[#F8F6F0]">PAQVERO Support</p>
                <p className="flex items-center gap-1.5 text-[11px] text-[#A5A49A]">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> Online — replies instantly
                </p>
              </div>
              <button onClick={() => setOpen(false)} aria-label="Close chat" data-testid="support-chat-close"
                className="flex h-8 w-8 items-center justify-center rounded-lg border border-line text-2 hoverable-t1">
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* messages — data-lenis-prevent: let the wheel scroll this box, not the page */}
            <div ref={scrollRef} data-testid="support-chat-messages" data-lenis-prevent
              className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
              {messages.length === 0 && (
                <div className="pt-2">
                  <p className="rounded-2xl rounded-bl-md bg-hover px-4 py-3 text-sm leading-relaxed text-2">
                    Hi! I'm the PAQVERO assistant. Ask me about orders, delivery, payments, PAQVERO Credit, invoices or returns.
                  </p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {QUICK_CHIPS.map((chip) => (
                      <button key={chip} onClick={() => send(chip)} data-testid={`support-chip-${chip.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                        className="rounded-full border border-amber-500/30 bg-amber-500/[0.07] px-3.5 py-1.5 text-xs font-semibold text-amber-300 transition-colors hover:bg-amber-500/20">
                        {chip}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {messages.map((m, i) => (
                <div key={i} data-testid={`support-msg-${i}`}
                  className={`max-w-[85%] px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === "user"
                      ? "ml-auto rounded-2xl rounded-br-md bg-amber-500 font-medium text-black"
                      : "rounded-2xl rounded-bl-md bg-hover text-2"
                  }`}>
                  {m.text || (streaming && i === messages.length - 1 ? (
                    <span className="flex gap-1 py-1" data-testid="support-typing">
                      {[0, 1, 2].map((d) => (
                        <span key={d} className="h-1.5 w-1.5 animate-bounce rounded-full bg-neutral-500" style={{ animationDelay: `${d * 150}ms` }} />
                      ))}
                    </span>
                  ) : null)}
                </div>
              ))}
              {leadOffer && leadDone === null && !streaming && (
                <LeadForm
                  sessionId={sid.current}
                  prefill={[...messages].reverse().find((m) => m.role === "user")?.text?.slice(0, 500) || ""}
                  onDone={(followUp) => setLeadDone(followUp === null ? "dismissed" : { followUp })}
                />
              )}
              {leadDone && leadDone !== "dismissed" && (
                <div data-testid="lead-success" className="rounded-2xl border border-emerald-500/25 bg-emerald-500/[0.06] px-4 py-3 text-sm leading-relaxed text-emerald-200">
                  {leadDone.followUp
                    ? "Thanks — the PAQVERO team has your message and will reach out to you."
                    : "Thanks — your message has been saved for the PAQVERO team. For immediate help, email care@paqvero.in or call +91 22 4890 1234 (Mon–Sat, 9 am–7 pm IST)."}
                </div>
              )}
            </div>

            {/* input */}
            <form onSubmit={(e) => { e.preventDefault(); send(); }}
              className="flex items-center gap-2 border-t border-line bg-[#F1EDE3] p-3">
              <input
                data-testid="support-chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about orders, delivery, credit…"
                maxLength={1000}
                className="h-11 flex-1 rounded-xl border border-line bg-input px-4 text-sm text-1 placeholder:text-[var(--pq-text3)] focus:border-amber-500/60 focus:outline-none"
              />
              <button type="submit" disabled={streaming || !input.trim()} data-testid="support-chat-send"
                aria-label="Send message"
                className="flex h-11 w-11 items-center justify-center rounded-xl bg-amber-500 text-black transition-colors hover:bg-amber-400 disabled:opacity-40">
                {streaming ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
